import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, UntypedFormControl, Validators } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { find, isEmpty, isNil, isUndefined, sortBy } from 'lodash';
import { distinctUntilChanged } from 'rxjs/operators';

import { CustomControlAbstract } from '@app/components/abstract/custom-control.abstract';
import { EIcon } from '@shared/enums/icon.enum';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { ValueLabelPair } from '@shared/interfaces/value-label-pair.interface';

@UntilDestroy()
@Component({
  selector: 'app-radio-button-dropdown',
  templateUrl: './radio-button-dropdown.component.html',
  styleUrls: ['./radio-button-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RadioButtonDropdownComponent<T>
  extends CustomControlAbstract<any>
  implements OnInit, DoCheck, OnDestroy, ControlValueAccessor, AfterContentChecked
{
  displayValue = '';
  private readonly labelInsideDefaultCss = 'nowrap pre-title';
  private readonly labelOutsideDefaultCss = 'nowrap body-small-bold';
  controlItems: IRadioButtonOption<T>[] = [];
  controlSectionItems: IRadioButtonOption<T>[][] = [];
  transformValueList: IRadioButtonOption<T>[] = [];
  hasValue = false;

  @Input() selectCss = 'body-small text-color dark';
  @Input() set labelCss(value: string) {
    this._labelCss = value;
  }
  get labelCss(): string {
    if (this._labelCss) return this._labelCss;

    return this.labelInside ? this.labelInsideDefaultCss : this.labelOutsideDefaultCss;
  }

  private _valueCss: string;
  @Input() set valueCss(value) {
    this._valueCss = value;
  }
  get valueCss() {
    if (this._valueCss) {
      return this._valueCss;
    }
    return 'ellipsis text-color dark ' + (this.labelInside ? '' : 'body-small');
  }

  private _optionCss: string;
  @Input() set optionCss(value) {
    this._optionCss = value;
  }
  get optionCss() {
    if (this._optionCss) {
      return this._optionCss;
    }
    return 'fade-truncate w-100 text-color dark ' + (this.labelInside ? 'pre-option' : 'body-small');
  }

  @Input() containerWidthCss = '';
  @Input() containerCss = 'display-flex align-items-center';
  @Input() label = '';
  @Input() labelTooltip = '';
  @Input() icon: EIcon;
  @Input() iconCss = 'dropdown-icon';
  @Input() iconTooltip = '';
  @Input() attrPlaceholder = '';
  @Input() onSelectValueContent: ValueLabelPair;
  // temporary for deprecated values (see MoveOutType)
  @Input() unsupportedValueLabel = 'select value';
  /*
   * issue available https://github.com/ng-select/ng-select/issues/2055
   * known workaround - https://github.com/Kyotu-Technology/noetic-frontend/pull/455
   */
  @Input() highlightWhenActive = false;
  @Input() attrAppendTo = '';
  @Input() allowSelectAll = false;
  @Input() multiSelect = false;
  @Input() attrDisable = false;
  @Input() labelInside = false;
  @Input() markAsInvalid = false;
  @Input() allowClear = true;
  @Input() allowSearch = true;
  @Input() displayLabelAsValue = true;
  @Input() labelRequired;
  @Input() showEmptyValue = false;
  @Input() activeOnFocus = false;
  @Input() errorSection = true;
  // TODO: move autoSortList to setter
  // otherwise parameters order matters
  @Input() autoSortList = true;

  @Input() set items(items: IRadioButtonOption<T>[] | IRadioButtonOption<T>[][]) {
    this.controlSectionItems = [];
    this.controlItems = [];
    this.transformValueList = [];

    if (Array.isArray(items?.[0])) {
      this.controlSectionItems = (items as IRadioButtonOption<T>[][]).map(section => this.sortSection(section));
      this.transformValueList = this.controlSectionItems.flat();
    } else {
      this.controlItems = this.sortSection(items as IRadioButtonOption<T>[]);
      this.transformValueList = [...this.controlItems];
    }
    this.transformValue();
  }
  @Input() set listLoading(listLoading: boolean) {
    this._listLoading = listLoading;
    this.cdr.markForCheck();
  }
  @Input() customListItemTemplate: TemplateRef<any>;

  @Input() allowAddNew = false;
  @Input() addNewLabel = '+ Add new item';
  @Output() addNewClicked = new EventEmitter();

  _labelCss = '';
  _listLoading = false;
  labelWidth: string;
  _labelElement: ElementRef<HTMLElement>;
  _focused = false;

  @ViewChild(NgSelectComponent) ngSelect: NgSelectComponent;
  @ViewChild('labelElement', { static: false }) set content(value: ElementRef<HTMLElement>) {
    if (!value) return;

    this._labelElement = value;
    this.labelWidth = `${value?.nativeElement?.clientWidth}px`;
    this.cdr.detectChanges();
  }

  constructor(
    @Self() @Optional() protected ngControl: NgControl,
    protected cdr: ChangeDetectorRef,
    @Optional() formDirective: FormGroupDirective
  ) {
    super(ngControl, cdr, formDirective);
  }

  ngDoCheck(): void {
    //It's obligatory as we can change reactive control that is binding with this control
    if (this.ngControl?.control && this.control !== this.ngControl.control) {
      this.initControl();
    }
  }

  ngAfterContentChecked(): void {
    if (!this.labelInside || !this._labelElement) return;

    this.labelWidth = `${this._labelElement?.nativeElement?.clientWidth}px`;
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    this.initControl();
  }

  private sortSection(items: IRadioButtonOption<T>[]) {
    return items?.length
      ? this.autoSortList
        ? sortBy(items as IRadioButtonOption<T>[], [item => item.sort, item => item.label?.toLowerCase()])
        : (items as IRadioButtonOption<T>[]) || []
      : [];
  }

  private initControl() {
    if (this.ngControl?.control) {
      this.control = this.ngControl?.control as UntypedFormControl;

      if (typeof this.labelRequired === 'undefined') {
        this.labelRequired = this.control.hasValidator(Validators.required);
      }
    }

    this.transformValue();
    this.control.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(value => this.transformValue());
    this.control.statusChanges.pipe(untilDestroyed(this)).subscribe(status => this.checkControlStatus(status));
    this.listenControlTouched(this.control)
      .pipe(untilDestroyed(this))
      .subscribe(() => this.cdr.markForCheck());

    if (this.ngControl?.control) {
      if (this.ngControl.control.errors) {
        this.errors = { ...this.ngControl.control.errors };
      }
      this.checkStatus();
    }
  }

  searchFunction(term: string, value: T): boolean {
    term = term.toLowerCase();
    const item = find(this.transformValueList, { value }) as IRadioButtonOption<T>;
    return item?.label?.toLowerCase()?.indexOf(term) > -1;
  }

  writeValue(value: any): void {
    if (value != this.control.value) {
      this.control.setValue(value);
    }
    this.transformValue();
  }

  valueChange(value: T) {
    this.onChanged(value);
  }

  ngOnDestroy(): void {}

  private transformValue() {
    const value = this.control?.value;
    const hasValue = Array.isArray(value) ? !isEmpty(value) : !isNil(value);
    if (hasValue || this.showEmptyValue) {
      this.hasValue = true;
      if (Array.isArray(value)) {
        if (value.length > 1) {
          this.displayValue = this.onSelectValueContent
            ? this.getCustomTransformValue(value.length)
            : `${value.length} item${value.length > 1 ? 's' : '' + ''}`;
        } else {
          const displayValue = this.transformValueList?.find(item => item.value === value[0])?.label;
          this.displayValue = isUndefined(displayValue) ? this.unsupportedValueLabel : displayValue;
        }
      } else if (this.displayLabelAsValue) {
        const displayValue = this.transformValueList?.find(item => item.value === value)?.label;
        this.displayValue = isUndefined(displayValue) ? this.unsupportedValueLabel : displayValue;
      } else {
        this.displayValue = value;
      }
    } else {
      this.hasValue = false;
    }

    this.cdr.detectChanges();
  }

  private getCustomTransformValue(count: number): string {
    if (!count) {
      return '';
    }

    return (count > 1 ? this.onSelectValueContent.multiple : this.onSelectValueContent.single).replace(
      this.onSelectValueContent.mask,
      `${count}`
    );
  }

  onSelectAll() {
    this.control.patchValue(this.controlItems.map(t => t.value));
    this.cdr.detectChanges();
  }

  onClearAll() {
    this.control.patchValue([]);
    this.cdr.detectChanges();
  }

  onAddNew() {
    this.addNewClicked.emit();
    this.ngSelect.close();
    this.cdr.detectChanges();
  }
}
